import React, {useState, useMemo} from 'react';
import {Button, Spin, Modal, Tag} from 'antd';
import {ModalDialog} from "../../components/ModalDialog";
import ResponsiveActionBar from "../../components/ResponsiveActionBar";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import {exportToCSV, exportToPDF, isFormComplete, isValidGhanaianNumber, notifyUser} from "../../util/util";
import { useUsers, useCreateUser, useUpdateUser, useUpdateUserStatus } from "../../hooks/api/useUsers";
import { formatCreatedAt } from "../../util/dateFormatter";

const UsersTable = () => {

    const [isOpen, setIsOpen] = useState(false)
    const [editModal, setEditModal] = useState(false)
    const [editStatusModal, setEditStatusModal] = useState(false)
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [userForm, setUserForm] = useState({firstName: '', lastName: '', email: '', phone: '', role: 'ADMIN', approvalPin: ''});
    const [editForm, setEditForm] = useState({ phone: '', role: 'ADMIN', approvalPin: '', id: ''});
    const [statusApprovalPin, setStatusApprovalPin] = useState('');

    // React Query hooks
    const {
        data: usersData,
        isLoading,
        refetch
    } = useUsers(pagination.current - 1, pagination.pageSize);

    const createUserMutation = useCreateUser();
    const updateUserMutation = useUpdateUser();
    const updateUserStatusMutation = useUpdateUserStatus();

    // Process and filter data for table display
    const filteredData = useMemo(() => {
        const tableData = usersData?.content || [];
        
        if (!searchText) return tableData;
        
        return tableData.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchText.toLowerCase())
            )
        );
    }, [usersData?.content, searchText]);

    // Update pagination total when data changes
    React.useEffect(() => {
        if (usersData?.totalElements !== undefined) {
            setPagination(prev => ({
                ...prev,
                total: usersData.totalElements
            }));
        }
    }, [usersData?.totalElements]);

    const toggleModal = () => {
        // Reset form and clear any stuck loading states when modal is toggled
        if (isOpen) {
            // Closing modal - reset form
            setUserForm({firstName: '', lastName: '', email: '', phone: '', role: 'ADMIN', approvalPin: ''});
            
            // Force reset mutation state if it's stuck
            if (createUserMutation.isLoading || createUserMutation.isPending) {
                createUserMutation.reset();
            }
        }
        setIsOpen(prevState => !prevState);
    }

    const toggleEditModal = (user) => {
        setEditForm({...editForm, phone: user.phone, role: user.role, id: user.id})
        setEditModal(prevState => !prevState)
    }

    const toggleEditStatusModal = (user=null) => {
        setEditForm(user)
        setEditStatusModal(prevState => !prevState)
    }

    const changeHandler = (e) => setUserForm({...userForm, [e.target.name]: e.target.value})
    const changeEditHandler = (e) => setEditForm({...editForm, [e.target.name]: e.target.value})

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
    };

    const columns = [
        { 
            title: 'Name', 
            dataIndex: 'name', 
            key: 'name', 
            width: 150,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Role', 
            dataIndex: 'role', 
            key: 'role', 
            width: 120,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Phone No', 
            dataIndex: 'phone', 
            key: 'phone', 
            width: 130,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Email', 
            dataIndex: 'email', 
            key: 'email', 
            width: 200,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Created On', 
            dataIndex: 'createdAt', 
            key: 'createdAt', 
            width: 150,
            render: (text) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {formatCreatedAt(text)}
                </div>
            )
        },
        {
            title: 'Status', 
            dataIndex: 'enabled', 
            key: 'enabled', 
            width: 100,
            render: (status) => (
                <Tag color={status ? 'green' : 'red'}>
                    {status ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        { 
            title: 'Edit', 
            dataIndex: 'edit', 
            key: 'edit', 
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Button 
                    type="link" 
                    size="small" 
                    onClick={() => toggleEditModal(record)}
                >
                    Edit <i className='bi bi-pencil'/>
                </Button>
            )
        },
        { 
            title: 'Action', 
            dataIndex: 'action', 
            key: 'action', 
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Button 
                    type="link" 
                    size="small" 
                    danger={record.enabled}
                    onClick={() => toggleEditStatusModal(record)}
                >
                    {record.enabled ? 'Deactivate' : 'Activate'}
                </Button>
            )
        }
    ];

    const createUser = () => {
      // Add phone validation check
      if (!isValidGhanaianNumber(userForm.phone)) {
          return notifyUser('error', 'Please enter a valid phone number in format 233 followed by 9 digits', 'Validation Error');
      }
      
      if (isFormComplete(userForm)){
          
          // Reset form state before mutation to prevent issues
          createUserMutation.mutate(userForm, {
              onSuccess: (data) => {
                  setUserForm({firstName: '', lastName: '', email: '', phone: '', role: 'ADMIN', approvalPin: ''});
                  toggleModal();
                  notifyUser('success','User invited successfully!', 'User created');
                  refetch();
              },
              onError: (error) => {
                  
                  let errorMessage = 'Failed to create user';
                  
                  // Handle specific authentication errors
                  if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
                      errorMessage = 'Authentication failed. Please login again.';
                  } else if (error?.message?.includes('403') || error?.message?.includes('Forbidden')) {
                      errorMessage = 'You don\'t have permission to create users.';
                  } else if (error?.message?.includes('timeout')) {
                      errorMessage = 'Request timed out. Please try again.';
                  } else if (error?.message) {
                      errorMessage = error.message;
                  }
                  
                  notifyUser('error', errorMessage, 'Error');
              },
              onSettled: () => {
              }
          })
      }else{
          notifyUser('error','All fields required!', 'Validation Error')
      }
    }

    const updateUser = () => {
      if (isFormComplete(editForm)){
          updateUserMutation.mutate({id: editForm.id, data: editForm}, {
              onSuccess: () => {
                  notifyUser('success','User edited successfully!', 'User Edit')
                  setEditModal(false)
                  setEditForm({ phone: '', role: 'ADMIN', approvalPin: '', id: ''})
                  refetch()
              },
              onError: (error) => {
                  notifyUser('error', error, 'Error')
              }
          })
      }else{
          notifyUser('error','All fields required!', 'Validation Error')
      }
    }

    const exportFunction = (type) => {
        if (type === 'pdf'){
            exportToPDF(columns, filteredData, 'User Management')
        }else{
            exportToCSV(filteredData, 'User Management')
        }
    }

    const submitUserStatus = () => {
        if (statusApprovalPin === '' || statusApprovalPin === null){
            return notifyUser('error', 'Please enter pin to continue', 'Validation Error')
        }
        
        updateUserStatusMutation.mutate({
            id: editForm.id, 
            data: {deactivate: true, approvalPin: statusApprovalPin}
        }, {
            onSuccess: (result) => {
                setEditStatusModal(false)
                notifyUser('success', result?.data?.message || 'User status updated', 'User Status')
                refetch()
            },
              onError: (error) => {
                  notifyUser('error', error, 'Error')
              }
        })
    }

    return (
        <div className="p-4">
            <ResponsiveActionBar
                title="User Management"
                searchValue={searchText}
                onSearchChange={handleSearch}
                onExportCSV={() => exportFunction('csv')}
                onExportPDF={() => exportFunction('pdf')}
                exportLoading={false}
                extraActions={
                    <button className="btn btn-primary" onClick={toggleModal}>
                        CREATE USER <i className="bi bi-plus-circle"/>
                    </button>
                }
            />
            
            <ResponsiveTableWrapper
                columns={columns}
                dataSource={filteredData}
                rowKey={(record, index) => record.id || record.phone || index}
                pagination={pagination}
                loading={isLoading}
                onChange={handleTableChange}
            />

            <ModalDialog isOpen={isOpen} title='Create User' closeFn={toggleModal}>
                <Spin spinning={createUserMutation.isLoading || createUserMutation.isPending}>
                    <div className="mb-4">
                        <omk-text-field label="First Name" value={userForm.firstName} name='firstName' onInput={changeHandler} type='text' className='mb-4'/>
                    </div>
                    <div className="mb-4">
                        <omk-text-field label="Last Name" value={userForm.lastName} name='lastName' onInput={changeHandler} type='text' className='mb-4'/>
                    </div>
                    <div className="mb-4">
                        <omk-text-field label="Email" value={userForm.email} name='email' onInput={changeHandler} type='email' className='mb-4'/>
                    </div>
                    <div className="mb-4">
                    <omk-text-field label="Phone Number" value={userForm.phone} type='text' className='mb-4' onInput={changeHandler} name='phone'
                      always-show-validation-message={userForm.phone === '' ? false : !isValidGhanaianNumber(userForm.phone)} custom-validation-message={!isValidGhanaianNumber(userForm.phone) ? 'Please enter number in format 233 followed by 9 digits without 0' : ''} />
                    </div>
                    <div className="mb-4">
                        <omk-select label="Role" value={userForm.role} name='role' onInput={changeHandler}>
                            <omk-select-option value="ADMIN">ADMIN</omk-select-option>
                        </omk-select>
                    </div>
                    <div className="mb-3">
                        <omk-otp-field
                            number-of-inputs="4"
                            resend-text=""
                            resend-button-label=""
                            label="Enter PIN"
                            name='approvalPin' value={userForm.approvalPin} onInput={changeHandler} />
                    </div>
                    <button className='btn btn-primary mt-5' onClick={createUser} disabled={createUserMutation.isLoading || createUserMutation.isPending}>
                        {(createUserMutation.isLoading || createUserMutation.isPending) ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating User...
                            </>
                        ) : (
                            <>CREATE USER <i className="bi bi-plus-circle"/></>
                        )}
                    </button>
                </Spin>
            </ModalDialog>
            <ModalDialog isOpen={editModal} title='Edit User' closeFn={() => setEditModal(false)}>
                <Spin spinning={updateUserMutation.isLoading}>
                    <div className="mb-4">
                        <omk-text-field label="Phone Number" value={editForm.phone} name='phone' onInput={changeEditHandler} type='text' className='mb-4'/>
                    </div>
                    <div className="mb-4">
                        <omk-select label="Role" value={editForm.role} name='role' onInput={changeEditHandler}>
                            <omk-select-option value="ADMIN">ADMIN</omk-select-option>
                        </omk-select>
                    </div>
                    <div className="mb-3">
                        <omk-otp-field
                            number-of-inputs="4"
                            resend-text=""
                            resend-button-label=""
                            label="Enter PIN"
                            name='approvalPin' value={editForm.approvalPin} onInput={changeEditHandler} />
                    </div>
                    <button className='btn btn-primary mt-3 w-50 mobile-full-width' onClick={updateUser}>UPDATE USER</button>
                </Spin>
            </ModalDialog>
            <Modal
                centered
                open={editStatusModal}
                destroyOnClose={true}
                closable={true}
                footer={null} onCancel={() => setEditStatusModal(false)}>
                <div align='center'>
                    <div className="p-5">
                        <omk-otp-field
                            number-of-inputs="4"
                            resend-text=""
                            resend-button-label=""
                            label="Enter PIN"
                            name='approvalPin' value={statusApprovalPin} onInput={e => setStatusApprovalPin(e.target.value)} />
                    </div>
                    <button className='btn btn-primary' onClick={submitUserStatus}>Deactive User</button>
                </div>
            </Modal>
        </div>
    );
};

export default UsersTable;